import { Suspense, useEffect, useRef } from 'react';

import { Box, styled } from '@mui/material';
import { useGetPaymentProcessor } from '@pflegenavi/frontend/api-nursing-home';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { useFeatureFlagModal } from '@pflegenavi/frontend/feature-flags-web';
import { DashboardLayout } from '@pflegenavi/frontend/layout';
import {
  DevLangSwitcher,
  localeStorage,
} from '@pflegenavi/frontend/localization-web-app';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import {
  useGetNursingHomeList,
  useGetServiceProviderPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import { ConnectionStatus } from '@pflegenavi/service-provider-shared/api-endpoints';
import {
  Login,
  RedirectResetPassword,
} from '@pflegenavi/shared-web/authentication';
import { useSyncLocaleWithServer } from '@pflegenavi/shared/frontend';
import { PageNotFound } from '@pflegenavi/web-components';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import { renderSwitchLanguageMenuItem } from '../components/general/SwitchLanguageMenuItem';
import { ServiceProviderProfileDetails } from '../components/profile/ServiceProviderProfileDetails';
import { ServiceProviderProfileSettings } from '../components/profile/ServiceProviderProfileSettings';
import ServiceProviderStripeOnboardingComplete from '../components/profile/ServiceProviderStripeOnboardingComplete';
import ServiceProviderStripeOnboardingRefresh from '../components/profile/ServiceProviderStripeOnboardingRefresh';
import { AddReceiptBatchPage } from '../pages/AddReceiptBatchPage';
import { DashboardPage } from '../pages/DashboardPage';
import EditReceiptBatchPage from '../pages/EditReceiptBatchPage';
import { PayoutsPage } from '../pages/PayoutsPage';
import { ProfilePage } from '../pages/ProfilePage';
import { ServicesOverviewPage } from '../pages/ServicesOverviewPage';
import { SetupBankAccountPage } from '../pages/SetupBankAccountPage';
import { SentryRoutes } from '../sentry';
import { ConnectApp } from './ConnectApp';
import { ScaEnrollmentButton } from '../components/sca/ScaEnrollmentButton';

export default function App(): JSX.Element {
  const { user } = useAuthentication();
  const location = useLocation();

  useSyncLocaleWithServer({ localeStorage });

  useEffect(() => {
    // @ts-expect-error Beacon is untyped
    if (!window.Beacon || !user) {
      return;
    }

    // @ts-expect-error Beacon is untyped
    window.Beacon('identify', {
      name: `${user.firstName} ${user.lastName}`,
      email: user?.email,
      tenant: 'service_provider',
      'user-id': user.userId,
      environment: window.location.hostname.startsWith('localhost')
        ? 'local'
        : window.location.hostname.includes('.staging.')
        ? 'staging'
        : 'production',
      application: 'Service Provider web',
    });
  }, [user]);

  useEffect(() => {
    // @ts-expect-error Beacon is untyped
    if (!window.Beacon) {
      return;
    }

    // @ts-expect-error Beacon is untyped
    window.Beacon('event', {
      type: 'page-viewed',
      url: location.pathname,
      title: document.title,
    });
    // @ts-expect-error Beacon is untyped
    window.Beacon('suggest');
    if (location && location.pathname === '/') {
      // @ts-expect-error Beacon is untyped
      window.Beacon('navigate', '/');
    }
  }, [location.pathname, location]);

  if (!user) {
    return (
      <>
        <Login />
      </>
    );
  }

  return <AppInner />;
}

const AppInner = () => {
  const navigate = useNavigate();

  const { FeatureFlagModalComponent } = useFeatureFlagModal();

  const { redirect } = useRedirectToConnectAccountPage();

  const paymentProcessor = useGetPaymentProcessor();
  const { data: serviceProvider } = useGetServiceProviderPhoenix();

  if (redirect) {
    return <ConnectApp />;
  }

  return (
    <Suspense fallback={<LoadingContainer />}>
      {FeatureFlagModalComponent}
      {!import.meta.env.PROD && <DevLangSwitcher />}
      <SentryRoutes>
        <Route
          path={'/reset-password/:token'}
          element={<RedirectResetPassword />}
        />
        <Route
          path={spAppMainPages.CONNECT_ACCOUNT}
          element={<Navigate to={spAppMainPages.DASHBOARD} replace />}
        />
        <Route
          path={spAppMainPages.NEW_INVITATION}
          element={<Navigate to={spAppMainPages.DASHBOARD} replace />}
        />
        <Route
          path={spAppMainPages.SETUP_BANK_ACCOUNT}
          element={<SetupBankAccountPage />}
        />
        <Route
          path={spAppMainPages.HOME}
          element={
            <DashboardLayout
              configKey="sp-web"
              helpUrl={
                'https://pn.helpscoutdocs.com/category/140-service-provider-webapp'
              }
              home={spAppMainPages.HOME}
              renderSwitchLanguageMenuItem={renderSwitchLanguageMenuItem}
              menuItems={
                paymentProcessor === 'mangopay' &&
                serviceProvider?.data?.mangopay_sca_enrolled === false
                  ? [<ScaEnrollmentButton key="sca-enrollment" />]
                  : undefined
              }
            />
          }
        >
          <Route path={spAppMainPages.DASHBOARD} element={<DashboardPage />} />
          <Route
            path={spAppMainPages.RECEIPT_BATCH_NEW}
            element={<AddReceiptBatchPage />}
          />
          <Route path={spAppMainPages.PAYOUTS} element={<PayoutsPage />} />
          <Route
            path={spAppMainPages.RECEIPT_BATCH_EDIT}
            element={<EditReceiptBatchPage />}
          />
          <Route
            path={spAppMainPages.SERVICES}
            element={<ServicesOverviewPage />}
          />
          <Route
            path={spAppMainPages.HOME}
            element={<Navigate to={spAppMainPages.DASHBOARD} replace />}
          />
          <Route
            path={spAppMainPages.WELCOME}
            element={<Navigate to={spAppMainPages.DASHBOARD} replace />}
          />
          <Route path={spAppMainPages.PROFILE} element={<ProfilePage />}>
            <Route index={true} element={<ServiceProviderProfileDetails />} />
          </Route>
          {paymentProcessor === 'mangopay' && (
            <Route path={spAppMainPages.SETTINGS} element={<ProfilePage />}>
              <Route
                index={true}
                element={<ServiceProviderProfileSettings />}
              />
            </Route>
          )}
          <Route
            path={spAppMainPages.STRIPE_ONBOARDING_COMPLETE}
            element={<ServiceProviderStripeOnboardingComplete />}
          />
          <Route
            path={spAppMainPages.STRIPE_ONBOARDING_REFRESH}
            element={<ServiceProviderStripeOnboardingRefresh />}
          />
          <Route
            path="*"
            element={
              <PageNotFound
                handleGoHomeClick={() => navigate(spAppMainPages.HOME)}
              />
            }
          />
        </Route>
      </SentryRoutes>
    </Suspense>
  );
};

const SCA_COMPLETED_SESSION_KEY = 'mangopay_sca_completed';
const SCA_COMPLETED_TIMESTAMP_KEY = 'mangopay_sca_completed_timestamp';
const SCA_FLAG_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

const useRedirectToConnectAccountPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Track when SCA validation is completed (controlStatus=VALIDATED in URL)
  // This persists across navigations until the backend confirms enrollment
  // or until the flag expires (5 minutes)
  const controlStatus = new URLSearchParams(location.search).get(
    'controlStatus'
  );
  if (controlStatus === 'VALIDATED') {
    sessionStorage.setItem(SCA_COMPLETED_SESSION_KEY, 'true');
    sessionStorage.setItem(SCA_COMPLETED_TIMESTAMP_KEY, Date.now().toString());
  }

  // Check if user is on dashboard - don't redirect them away
  // This prevents race conditions when returning from MangoPay SCA
  const isOnDashboard = location.pathname.includes('/dashboard');

  const { user } = useAuthentication();
  const globalServiceProviderId = user?.userId;

  const result = useGetNursingHomeList({});
  const { data: nursingHomeList } = result;

  // Get service provider to check MangoPay setup status
  const { data: serviceProvider } = useGetServiceProviderPhoenix();

  const hasActiveNursingHome = Boolean(
    nursingHomeList?.find(
      (nursingHome) => nursingHome.connectionStatus === ConnectionStatus.Active
    )
  );
  const pendingNursingHome = nursingHomeList?.find(
    (nursingHome) =>
      nursingHome.connectionStatus === ConnectionStatus.InvitePending
  );

  const hasPendingNursingHome = Boolean(pendingNursingHome);

  // Check MangoPay setup status (matching mobile's onboarding-check logic)
  const isMangopay =
    serviceProvider?.data?.payment_service_provider === 'mangopay';

  // Step 1 & 2: MangoPay user not created yet
  const mangopayUserMissing =
    isMangopay && !serviceProvider?.data?.mangopay_user_id;

  // SCA enrollment required (user created but SCA not enrolled)
  const mangopayScaRequiredByBackend =
    isMangopay &&
    serviceProvider?.data?.mangopay_user_id &&
    !serviceProvider?.data?.mangopay_sca_enrolled;

  // Clear the SCA completed flag once backend confirms enrollment
  // This allows future SCA requirements to trigger redirects again
  if (serviceProvider?.data?.mangopay_sca_enrolled) {
    sessionStorage.removeItem(SCA_COMPLETED_SESSION_KEY);
    sessionStorage.removeItem(SCA_COMPLETED_TIMESTAMP_KEY);
  }

  // Don't redirect for SCA if we just completed it (race condition protection)
  // The backend may not have processed the MangoPay callback yet
  // Flag expires after 5 minutes to prevent stuck states if webhook fails
  const scaFlagValue = sessionStorage.getItem(SCA_COMPLETED_SESSION_KEY);
  const scaTimestamp = sessionStorage.getItem(SCA_COMPLETED_TIMESTAMP_KEY);
  const parsedTimestamp = scaTimestamp ? parseInt(scaTimestamp, 10) : NaN;
  const isScaFlagExpired =
    !scaTimestamp ||
    isNaN(parsedTimestamp) ||
    Date.now() - parsedTimestamp > SCA_FLAG_EXPIRY_MS;

  // Clear invalid or expired SCA flags to prevent stuck states
  if (scaFlagValue && isScaFlagExpired) {
    sessionStorage.removeItem(SCA_COMPLETED_SESSION_KEY);
    sessionStorage.removeItem(SCA_COMPLETED_TIMESTAMP_KEY);
  }

  const scaJustCompleted = scaFlagValue && !isScaFlagExpired;
  const mangopayScaRequired = mangopayScaRequiredByBackend && !scaJustCompleted;

  // Step 3: Bank account setup needed (user created, SCA done, but no bank account)
  // Note: On web, users can skip bank account setup. On mobile, skipping is disabled.
  const mangopayBankAccountNeeded =
    isMangopay &&
    serviceProvider?.data?.mangopay_user_id &&
    serviceProvider?.data?.mangopay_sca_enrolled &&
    !serviceProvider?.data?.mangopay_bank_account_id;

  useEffect(() => {
    // Don't redirect if user is already on an onboarding page
    // This prevents race conditions when returning from MangoPay SCA
    const pathname = location.pathname;

    // Skip redirects when:
    // 1. User is on onboarding pages (/connect-account, /setup-bank-account)
    // 2. User is on dashboard (may have just returned from MangoPay SCA)
    if (
      pathname.includes('/connect-account') ||
      pathname.includes('/setup-bank-account') ||
      pathname.includes('/dashboard')
    ) {
      return;
    }

    // Priority 1: Pending invitation
    if (hasPendingNursingHome) {
      if (!hasActiveNursingHome) {
        navigateRef.current(
          {
            pathname: `${spAppMainPages.CONNECT_ACCOUNT}?tenant=${
              pendingNursingHome!.tenant
            }&nursingHomeId=${
              pendingNursingHome!.nursingHomeId
            }&globalServiceProviderId=${
              pendingNursingHome!.globalServiceProviderId
            }`,
          },
          { replace: true }
        );
      } else {
        navigateRef.current(
          {
            pathname: `${spAppMainPages.NEW_INVITATION}?tenant=${
              pendingNursingHome!.tenant
            }&nursingHomeId=${
              pendingNursingHome!.nursingHomeId
            }&globalServiceProviderId=${
              pendingNursingHome!.globalServiceProviderId
            }`,
          },
          { replace: true }
        );
      }
      return; // Exit early if handling invitation
    }

    // Priority 2: MangoPay user account setup incomplete (Steps 1 & 2)
    if (mangopayUserMissing && globalServiceProviderId) {
      navigateRef.current(
        {
          pathname: `${spAppMainPages.CONNECT_ACCOUNT}?globalServiceProviderId=${globalServiceProviderId}`,
        },
        { replace: true }
      );
      return;
    }

    // Priority 3: MangoPay SCA enrollment required
    if (mangopayScaRequired && globalServiceProviderId) {
      navigateRef.current(
        {
          pathname: `${spAppMainPages.CONNECT_ACCOUNT}?globalServiceProviderId=${globalServiceProviderId}`,
        },
        { replace: true }
      );
      return;
    }

    // Priority 4: MangoPay bank account setup needed (Step 3)
    if (mangopayBankAccountNeeded) {
      navigateRef.current(
        {
          pathname: spAppMainPages.SETUP_BANK_ACCOUNT,
        },
        { replace: true }
      );
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasActiveNursingHome,
    hasPendingNursingHome,
    pendingNursingHome?.tenant,
    pendingNursingHome?.nursingHomeName,
    pendingNursingHome?.nursingHomeId,
    pendingNursingHome?.globalServiceProviderId,
    mangopayUserMissing,
    mangopayScaRequired,
    mangopayBankAccountNeeded,
    globalServiceProviderId,
    location.pathname, // isOnDashboard is derived from this
  ]);

  // Note: mangopayBankAccountNeeded is NOT included in redirect because
  // SetupBankAccountPage is rendered by AppInner, not ConnectApp.
  // The navigation happens via useEffect, and the page renders normally.

  // Don't redirect to ConnectApp if:
  // 1. User is on setup-bank-account page (ConnectApp doesn't have a route for it)
  // 2. User just returned from MangoPay SCA (ref is set when controlStatus was seen)
  // These checks prevent race conditions where backend hasn't processed the callback yet
  const isOnSetupBankAccount = location.pathname.includes(
    '/setup-bank-account'
  );

  const redirect =
    !isOnSetupBankAccount &&
    !isOnDashboard &&
    (hasPendingNursingHome || mangopayUserMissing || mangopayScaRequired);

  return { redirect };
};

const LoadingContainer = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  })
);
